/**
 * Money amounts as decimal strings, worked in exact integer units (BigInt)
 * instead of floats. A float can round 0,00059641 up on its way to
 * `toFixed(8)`, or call an amount that fits the balance with its fee
 * a hair too large; the server checks in exact decimals, and so must the app.
 */

/** The most fractional digits the app sends or compares: the server's amount precision. */
export const MAX_AMOUNT_DECIMALS = 8;

const pow10 = (n: number): bigint => BigInt(10) ** BigInt(n);

/**
 * "1 234,567" or "1234.567" → "1234.567": the keypad's display form (thin or
 * regular spaces, comma) or the backend's, as a plain decimal string. Not a
 * number → "".
 */
export const normalizeDecimal = (value: string): string => {
  const s = value.replace(/[\s  ]/g, '').replace(',', '.');
  return /^\d*\.?\d*$/.test(s) && /\d/.test(s) ? s : '';
};

/** A decimal string as integer units at `scale` places, cut (never rounded) — "1.239", 2 → 123n. */
const toUnits = (value: string, scale: number): bigint => {
  const s = normalizeDecimal(value);
  if (!s) {
    return BigInt(0);
  }
  const [whole, fraction = ''] = s.split('.');
  return BigInt((whole || '0') + fraction.slice(0, scale).padEnd(scale, '0'));
};

/** Integer units at `scale` places back to a decimal string with no trailing zeros — 123n, 2 → "1.23". */
const fromUnits = (units: bigint, scale: number): string => {
  if (scale === 0) {
    return units.toString();
  }
  const digits = units.toString().padStart(scale + 1, '0');
  const whole = digits.slice(0, -scale);
  const fraction = digits.slice(-scale).replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole;
};

/** How many fractional digits a decimal string carries. */
const scaleOf = (value: string): number => {
  const s = normalizeDecimal(value);
  const dot = s.indexOf('.');
  return dot < 0 ? 0 : s.length - dot - 1;
};

/**
 * The amount for a request body: the typed or prefilled amount cut — not
 * rounded — to `maxDecimals` places. `toFixed(8)` rounded half up, so a
 * longer amount (Max on an 18-place token) could ask for more than the
 * balance holds.
 */
export const toRequestAmount = (display: string, maxDecimals: number = MAX_AMOUNT_DECIMALS): string => {
  const scale = Math.max(0, Math.min(maxDecimals, MAX_AMOUNT_DECIMALS));
  return fromUnits(toUnits(display, scale), scale);
};

/**
 * The largest amount, at `decimals` places, that the balance covers together
 * with a fee of `feeRate` × amount charged on top — the withdrawal's "Max".
 * `available` and `feeRate` are the backend's decimal strings ("0.02" is 2 %).
 */
export const maxAmountWithFee = (available: string, feeRate: string, decimals: number): string => {
  const scale = Math.max(0, Math.min(decimals, MAX_AMOUNT_DECIMALS));
  const availableScale = scaleOf(available);
  const rateScale = scaleOf(feeRate);
  const availableUnits = toUnits(available, availableScale);
  const rateUnits = toUnits(feeRate, rateScale);
  // amount × (1 + rate) ≤ available, with amount = M / 10^scale:
  // M ≤ available × 10^(scale + rateScale) / ((10^rateScale + rate) × 10^availableScale).
  const numerator = availableUnits * pow10(scale + rateScale);
  const denominator = (pow10(rateScale) + rateUnits) * pow10(availableScale);
  return fromUnits(numerator / denominator, scale);
};

/** Whether `amount` plus `feeRate` × `amount` fits in `available` — exactly, as the server checks it. */
export const fitsWithFee = (amount: string, available: string, feeRate: string): boolean => {
  const amountScale = scaleOf(amount);
  const availableScale = scaleOf(available);
  const rateScale = scaleOf(feeRate);
  const lhs = toUnits(amount, amountScale) * (pow10(rateScale) + toUnits(feeRate, rateScale)) * pow10(availableScale);
  const rhs = toUnits(available, availableScale) * pow10(amountScale + rateScale);
  return lhs <= rhs;
};
